import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { BrandLogoFull, BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset successful");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-950 text-white relative overflow-hidden bwz-grid-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 opacity-90" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <BrandLogoFull size="md" testId="reset-brand" />
            <div>
              <div className="font-display font-bold text-xl">Brandon Wheelz</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mt-0.5">OPS Central</div>
            </div>
          </div>
          <div className="space-y-6 max-w-md">
            <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight">
              Set a new password.
            </h1>
            <p className="text-zinc-400 text-base leading-relaxed">
              Pick something memorable but strong. You'll use this to sign in next time.
            </p>
          </div>
          <div className="text-xs text-zinc-500 font-mono">v1.0 • internal use</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-zinc-50">
        <Card className="w-full max-w-md p-8 border-zinc-200 shadow-sm" data-testid="reset-card">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <BrandLogo to={null} size="sm" testId="reset-mobile-brand" />
            <div>
              <div className="font-display font-bold">Brandon Wheelz</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">OPS Central</div>
            </div>
          </div>

          {!token ? (
            <div className="text-center py-4" data-testid="reset-no-token">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="font-display text-xl font-semibold tracking-tight">Invalid link</h2>
              <p className="text-sm text-zinc-500 mt-2">
                This reset link is missing a token. Request a new one from the forgot password page.
              </p>
              <Link
                to="/forgot-password"
                data-testid="request-new-link"
                className="inline-block mt-5 text-xs text-zinc-950 underline underline-offset-2"
              >
                Request a new reset link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="font-display text-xl font-semibold tracking-tight">Password updated</h2>
              <p className="text-sm text-zinc-500 mt-2">
                Redirecting you to sign in…
              </p>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl font-semibold tracking-tight">Reset password</h2>
              <p className="text-sm text-zinc-500 mt-1 mb-6">Enter and confirm your new password.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-600">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-password-input"
                    className="mt-1.5"
                    placeholder="At least 6 characters"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm" className="text-xs uppercase tracking-wider text-zinc-600">Confirm password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-confirm-input"
                    className="mt-1.5"
                  />
                </div>
                {error && <div className="text-xs text-red-600" data-testid="reset-error">{error}</div>}
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="reset-submit-button"
                  className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Update password
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-zinc-100">
            <Link
              to="/login"
              data-testid="back-to-login-link"
              className="text-xs text-zinc-600 hover:text-zinc-950 transition-colors"
            >
              ← Back to sign in
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
